import React from "react";
import {
  Paper,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Box,
  Checkbox,
  Typography,
} from "@material-ui/core";
import CustomPagination from "../../../../components/Common/CustomPagination";
import moment from "moment";

const VesselList = ({
  vesselContracts,
  onChangeVesselPagination,
  vesselFilters,
  onClickVessel,
  selectedVessel,
  isLoading,
  modelType,
  contractType,
}) => {
  const handleCheckboxChange = (vessel) => {
    const newSelection =
      selectedVessel && selectedVessel._id === vessel._id ? null : vessel;
    onClickVessel(newSelection);
  };

  // ✅ Normalize both contract and plain vessel structures
  const normalizedVessels = Array.isArray(vesselContracts)
    ? vesselContracts.map((item) => {
      const isContract = !!item.contractType || !!item?.contractName;

      if (isContract) {
        // Contract-based vessel
        const vesselData =
          item?.vesselDetails?.fleetList?.[0] || item?.vesselDetails || {};
        let vesselName = "-";
        let dwt = "-";
        try {
          if (vesselData?.vessel) {
            const parsed = JSON.parse(vesselData.vessel);
            vesselName = parsed?.vesselName || "-";
            dwt = parsed?.vesselDwt || "-";
          }
        } catch {
          vesselName = vesselData.vesselName || "-";
        }

        return {
          _id: item._id,
          isContract: true,
          vesselName,
          dwt,
          port: vesselData.port || "-",
          fixtureId: item.vesselFixtureId || "-",
          cpDate: item.cpDate || null,
          counterparty: item.counterParty || "-",
          contractingCompany: item.contractingCompany || "-",
          status: item.status || "active",
          estId: item.estId || "-",
          raw: item,
        };
      } else {
        // Plain vessel from userFleets API
        let vesselName = item.vesselName || "-";
        let dwt = item.dwt || item.vesselDwt || "-";
        try {
          if (item.vessel && typeof item.vessel === "string") {
            const parsed = JSON.parse(item.vessel);
            vesselName = parsed?.vesselName || vesselName;
            dwt = parsed?.vesselDwt || dwt;
          }
        } catch {
          // ignore
        }

        return {
          _id: item._id,
          isContract: false,
          vesselName,
          dwt,
          port: item.port || item.vesselOpeningPort || "-",
          fixtureId: item.vesselId || "-",
          cpDate: item.dateOpen || item.createdDate || null,
          status: "active",
          raw: item,
        };
      }
    })
    : [];

  // ✅ Filter irrelevant vessels
  const filteredVessels = normalizedVessels.filter((vessel) => {
    if (
      vessel.isContract &&
      ((vessel.raw.contractType === "vessel-vessel" &&
        modelType === "vessel-cargo") ||
        vessel.status === "close")
    ) {
      return false;
    }
    return true;
  });

  // ✅ Should we show contract-related columns?
  const showContractColumns =
    modelType !== "general" || contractType === "vessel-cargo";

  return (
    <Box>
      {isLoading ? (
        <Box display="flex" justifyContent="center" padding={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {vesselFilters?.totalPages > vesselFilters?.limit && (
            <CustomPagination
              onChangePagination={onChangeVesselPagination}
              filters={vesselFilters}
              totalPage={vesselFilters?.totalPages}
            />
          )}

          <TableContainer
            component={Paper}
            className="scroll-thin"
            style={{ maxHeight: "500px", overflow: "auto" }}
          >
            <Table className="universal-table contract-estimate-selection-table fit-content-mobile" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Select</TableCell>
                  <TableCell>Vessel Name</TableCell>
                  <TableCell>DWT</TableCell>
                  <TableCell>Port</TableCell>

                  {/* ✅ Only show for non-general or general + vessel-cargo */}
                  {showContractColumns && (
                    <>
                      <TableCell>Estimation ID</TableCell>
                      <TableCell>Vessel Fixture ID</TableCell>
                    </>
                  )}

                  <TableCell>CP / Created Date</TableCell>

                  {showContractColumns && (
                    <>
                      <TableCell>Counter Party</TableCell>
                      <TableCell>Contracting Company</TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredVessels.length > 0 ? (
                  filteredVessels.map((vessel) => {
                    const isSelected =
                      selectedVessel && selectedVessel._id === vessel._id;

                    return (
                      <TableRow
                        key={vessel._id}
                        hover
                        className={isSelected ? "contract-estimate-selected" : ""}
                        onClick={() => handleCheckboxChange(vessel.raw)}
                        onKeyDown={(event) => {
                          if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) {
                            event.preventDefault();
                            handleCheckboxChange(vessel.raw);
                          }
                        }}
                        tabIndex={0}
                        aria-selected={Boolean(isSelected)}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isSelected}
                            color="primary"
                            style={{ color: isSelected ? "#313C4C" : "#64748b" }}
                            onChange={() => handleCheckboxChange(vessel.raw)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </TableCell>
                        <TableCell>{vessel.vesselName}</TableCell>
                        <TableCell>{vessel.dwt}</TableCell>
                        <TableCell>{vessel.port}</TableCell>

                        {showContractColumns && (
                          <>
                            <TableCell>{vessel.estId || "-"}</TableCell>
                            <TableCell>{vessel.fixtureId}</TableCell>
                          </>
                        )}

                        <TableCell>
                          {vessel.cpDate
                            ? moment(vessel.cpDate).format("DD-MM-YYYY")
                            : "-"}
                        </TableCell>

                        {showContractColumns && (
                          <>
                            <TableCell>{vessel.counterparty}</TableCell>
                            <TableCell>{vessel.contractingCompany}</TableCell>
                          </>
                        )}
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body1" color="textSecondary">
                        {!vesselContracts?.length
                          ? "No vessels available"
                          : "No vessels match the current criteria"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {vesselFilters?.totalPages > vesselFilters?.limit && (
            <CustomPagination
              onChangePagination={onChangeVesselPagination}
              filters={vesselFilters}
              totalPage={vesselFilters?.totalPages}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default VesselList;
